import React, { useState, useEffect } from "react";
import { X, Download, FileText, Image as ImageIcon, CheckCircle2, Lock } from "lucide-react";
import api, { apiError, BACKEND_URL } from "../lib/api";
import { track, newEventId } from "../lib/track";
import { toast } from "sonner";

const mediaUrl = (u) => (!u ? u : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = mediaUrl(url);
    a.download = filename;
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export default function GuideDownloadModal({ open, guide, onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [unlocked, setUnlocked] = useState(null);

    useEffect(() => {
        if (open) { setName(""); setEmail(""); setBusy(false); setUnlocked(null); }
    }, [open, guide]);

    if (!open || !guide) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setBusy(true);
        try {
            const eid = newEventId();
            const { data } = await api.post(`/guides/${guide.slug}/unlock`, { name, email, event_id: eid });
            setUnlocked(data);
            track("Lead", { content_name: guide.title, content_category: "resource" }, { eventID: eid });
            triggerDownload(data.pdf || data.image, `${guide.slug}.pdf`);
            toast.success("Unlocked! Your download is starting.");
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="guide-download-modal">
            <div className="absolute inset-0 bg-espresso/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-white shadow-2xl">
                <button onClick={onClose} className="absolute top-4 right-4 text-neutral-400 hover:text-ink" data-testid="guide-modal-close"><X className="h-5 w-5" /></button>

                {!unlocked ? (
                    <form onSubmit={submit} className="p-9 text-center">
                        <div className="mx-auto h-14 w-14 rounded-full bg-cream border border-[#e0d6c2] grid place-items-center">
                            <Lock className="h-6 w-6 text-brand-golddark" />
                        </div>
                        <p className="overline mt-5 mb-2">Free Download</p>
                        <h3 className="font-display font-semibold text-2xl">{guide.title}</h3>
                        <p className="mt-3 text-neutral-500 text-sm">Enter your details and we'll unlock the download instantly, plus email you a copy.</p>
                        <div className="mt-6 space-y-3 text-left">
                            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="guide-name-input" className="w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink text-sm" />
                            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="guide-email-input" className="w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink text-sm" />
                        </div>
                        <button type="submit" disabled={busy} className="btn-primary w-full mt-5 disabled:opacity-60" data-testid="guide-unlock-btn">
                            <Download className="h-4 w-4" /> {busy ? "Unlocking…" : "Unlock & Download"}
                        </button>
                        <p className="mt-3 text-[11px] text-neutral-400">No spam. Unsubscribe anytime.</p>
                    </form>
                ) : (
                    <div className="p-9 text-center" data-testid="guide-unlocked-state">
                        <div className="mx-auto h-14 w-14 rounded-full bg-green-50 border border-green-200 grid place-items-center">
                            <CheckCircle2 className="h-6 w-6 text-green-600" />
                        </div>
                        <p className="overline mt-5 mb-2">You're all set</p>
                        <h3 className="font-display font-semibold text-2xl">Download {guide.title}</h3>
                        <p className="mt-3 text-neutral-500 text-sm">Your PDF download has started. Grab either format below.</p>
                        <div className="mt-6 space-y-3">
                            <button onClick={() => triggerDownload(unlocked.pdf || unlocked.image, `${guide.slug}.pdf`)} className="btn-dark w-full" data-testid="guide-download-pdf">
                                <FileText className="h-4 w-4" /> Download PDF (print ready)
                            </button>
                            <button onClick={() => triggerDownload(unlocked.image, `${guide.slug}.png`)} className="btn-outline w-full" data-testid="guide-download-image">
                                <ImageIcon className="h-4 w-4" /> Download Image
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
